import { ComparatorContext, IProduct } from '@kizi/comparator-core';
import { Div, DivProps, Label, P, Span } from '@kizi/core-components';
import React, { MouseEventHandler, useContext, useMemo } from 'react';
import { formatPrice, isMobile } from '../../utils/global';

export const DEFAULT_DISCOUNT = 10;

export interface PriceProps extends DivProps {
  data?: IProduct;
  decimals?: number;
  discount?: number;
  initialValue?: number;
  hideIfZero?: boolean;
  label?: string;
  onDivElementClick?: MouseEventHandler;
  onLabelClick?: MouseEventHandler;
  value: number;
  type?: string;
  pricePostfix?: string;
  activeDiscount?: boolean;
  labelProps?: any;
  variant?: string;
  hidePriceSymbol?: boolean;
}

export const PriceV2 = ({
  price,
  hideSymbol = false,
  centsProps = {},
  mainPriceProps = {},
  ...rest
}) => {
  // Regular expression to capture the symbol, main price, and cents, handling thousands separators
  const priceRegex = /([^\d\s]+)?\s*([\d.]+)[,](\d+)/;
  const match = price.match(priceRegex);

  if (!match) {
    console.error('Invalid price format:', price);
    return null;
  }

  const [, symbol = '€', rawMainPrice, cents] = match;

  // Replace dots in rawMainPrice for parsing and format it with localized thousands separators
  const mainPriceNumber = parseInt(rawMainPrice.replace(/\./g, ''), 10);
  const formattedMainPrice = new Intl.NumberFormat('de-DE').format(
    mainPriceNumber,
  );

  return (
    <Div display={'flex'} justifyContent={'flex-end'} {...rest}>
      <P
        variant="baseBodyImportant"
        fontSize={'40px'}
        lineHeight={'92%'}
        {...mainPriceProps}
      >
        {!hideSymbol && symbol}&nbsp;
        {formattedMainPrice}
      </P>
      <P
        variant="baseBodyImportant"
        lineHeight={'unset'}
        alignSelf={'start'}
        {...centsProps}
      >
        ,{cents}
      </P>
    </Div>
  );
};

export const Price: React.FC<PriceProps> = (props: PriceProps) => {
  const {
    decimals = 2,
    discount = 0,
    hideIfZero,
    initialValue = 0,
    label,
    onDivElementClick,
    onLabelClick,
    value,
    type,
    pricePostfix = '',
    activeDiscount = true,
    labelProps,
    hidePriceSymbol,
    ...restProps
  } = props;

  const app = useContext(ComparatorContext);
  const isEnergy =
    type === 'energyAuction' || app.name === 'energy' || app.name === 'energy-auction';
  const isHomeOrTravel = ['home', 'os-home', 'os-travel', 'travel'].includes(
    app.name,
  );
  const isHome = ['home', 'os-home'].includes(app.name);

  const renderedMainPart = useMemo(() => {
    const _value = (value || 0).toFixed(decimals);
    let firstPart,
      secondPart = '';

    if (type === 'savings') {
      firstPart = `${_value.replace('.', ',')}%`;
    } else {
      const parts = _value.split('.');
      firstPart = `€${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
      secondPart = decimals === 2 ? `,${parts[1]}` : '';
    }

    return isHomeOrTravel && isMobile() ? (
      <PriceV2
        price={formatPrice(_value)}
        mainPriceProps={{ fontSize: '40px' }}
        centsProps={{ fontSize: '24px' }}
      />
    ) : (
      <Div>
        <Span>{firstPart}</Span>
        <Span>{secondPart}</Span>
      </Div>
    );
  }, [value, decimals]);

  if (value === 0 && hideIfZero) return null;

  return (
    <Div data-testid="price-div" {...restProps} onClick={onDivElementClick}>
      <Div>
        {renderedMainPart}
        {activeDiscount &&
          discount > 0 &&
          !initialValue &&
          !isEnergy && (
            <Div
              className="discount"
              display="block"
              width="fit-content"
              variant="paragraphs.warningSmall"
            >
              Actiekorting!
            </Div>
          )}
        {initialValue > 0 && initialValue !== value && !isHome && (
          <Div
            className="lineThrough"
            display="block"
            width="fit-content"
            variant="paragraphs.lineThrough"
          >
            {`${formatPrice(initialValue)} ${pricePostfix}`}
          </Div>
        )}
      </Div>
      {label && (
        <Label
          mt={
            (type === 'health' || isHomeOrTravel) && isMobile()
              ? '0!important'
              : null
          }
          data-testid="price-label"
          display="block"
          onClick={onLabelClick}
          style={{
            cursor: onLabelClick !== undefined ? 'pointer' : '',
            ...labelProps,
          }}
          variant="paragraphs.captionRegularSmall"
        >
          {label}
        </Label>
      )}
    </Div>
  );
};
